import React, { useEffect, useMemo, useState } from 'react'
import { Button, Checkbox, Chip, FormControlLabel } from '@mui/material'
import { getImpulsaPanel } from 'src/services/preciosService'
import TablaPrecios from './TablaPrecios'
import { fmt } from './columnasPrecios'

/**
 * Pestaña Impulsa: lo SINCRONIZADO, no todo el catálogo (pedido del 2026-11-04).
 *
 * Impulsa se actualiza solo cuando SAP confirma TODO un lote (callback). Arriba quedan los lotes que todavía
 * esperan a SAP; abajo, cada respuesta de Impulsa por material: precio enviado, el que tenía y el resultado.
 */
const RESULTADOS = {
  ACTUALIZADO: { color: 'success', txt: 'Actualizado' },
  SIN_CAMBIO: { color: 'success', txt: 'Sin cambio' },
  NO_EXISTE: { color: 'error', txt: 'No existe en Impulsa' },
  AMBIGUO: { color: 'error', txt: 'Ambiguo (código repetido)' },
  PRECIO_INVALIDO: { color: 'error', txt: 'Precio inválido' },
  DUPLICADO_EN_LOTE: { color: 'warning', txt: 'Duplicado en el lote' },
}

const chipResultado = (r) => {
  const s = RESULTADOS[r] || { color: 'error', txt: String(r || '—').replace(/_/g, ' ').toLowerCase() }
  return <Chip size="small" color={s.color} label={s.txt} variant="outlined" />
}

const fecha = (v) => (v ? new Date(v).toLocaleString('es-CO', { dateStyle: 'short', timeStyle: 'short' }) : '—')

const ImpulsaTab = () => {
  const [datos, setDatos] = useState({ registro: [], esperando: [], produccion: false })
  const [soloError, setSoloError] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')

  const cargar = async (soloErr = soloError) => {
    setIsLoading(true)
    const r = await getImpulsaPanel(soloErr)
    setIsLoading(false)
    if (!r || Number(r.success) !== 1) {
      setError((r && r.error) || 'No se pudo leer el registro de Impulsa.')
      return
    }
    setError('')
    setDatos({ registro: r.registro || [], esperando: r.esperando || [], produccion: Boolean(r.produccion) })
  }

  useEffect(() => {
    cargar(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const columnas = useMemo(() => [
    { accessorKey: 'fecha', header: 'Fecha', size: 130, Cell: ({ row }) => fecha(row.original.fecha) },
    { accessorKey: 'material', header: 'Material', size: 110 },
    {
      id: 'moto',
      header: 'Moto',
      size: 220,
      accessorFn: (r) => `${r.marca || ''} ${r.modelo || ''} ${r.anio_modelo || ''}`.trim(),
    },
    { accessorKey: 'color', header: 'Color', size: 160 },
    {
      accessorKey: 'precio_anterior',
      header: 'Antes en Impulsa',
      size: 130,
      Cell: ({ row }) => (row.original.precio_anterior ? fmt(row.original.precio_anterior) : '—'),
    },
    {
      accessorKey: 'precio',
      header: 'Precio enviado',
      size: 130,
      Cell: ({ row }) => (row.original.precio ? fmt(row.original.precio) : '—'),
    },
    { accessorKey: 'resultado', header: 'Resultado', size: 170, Cell: ({ row }) => chipResultado(row.original.resultado) },
    { accessorKey: 'lote_id', header: 'Lote SAP', size: 90 },
  ], [])

  const conError = datos.registro.filter((r) => Number(r.ok) !== 1).length

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontWeight: 700, fontSize: 15, color: '#0f2439' }}>Sincronización con Impulsa</div>
        <div style={{ fontSize: 12.5, color: '#6b7684' }}>
          El precio de lista se manda a Impulsa solo cuando SAP confirma todo el lote.
          {!datos.produccion && (
            <b style={{ color: '#8a5a00' }}> Impulsa está en simulado (impulsa_produccion = 0): no se está llamando.</b>
          )}
        </div>
      </div>

      {error && <div style={{ color: '#b42318', marginBottom: 10 }}>{error}</div>}

      {/* Lo que espera a SAP: llega a Impulsa cuando SAP confirme */}
      <div className="precios-card" style={{ marginBottom: 14 }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: '#0f2439', marginBottom: 6 }}>
          Esperando a SAP ({datos.esperando.length})
        </div>
        {datos.esperando.length === 0 ? (
          <div style={{ fontSize: 12.5, color: '#6b7684' }}>Ningún lote esperando confirmación.</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: '#56606b', borderBottom: '1px solid #e3e7ec' }}>
                <th style={{ padding: '4px 8px' }}>Lote</th>
                <th style={{ padding: '4px 8px' }}>Enviado</th>
                <th style={{ padding: '4px 8px' }}>Motos</th>
                <th style={{ padding: '4px 8px' }}>Confirmados</th>
                <th style={{ padding: '4px 8px' }}>Estado</th>
              </tr>
            </thead>
            <tbody>
              {datos.esperando.map((l) => (
                <tr key={l.lote_id} style={{ borderBottom: '1px solid #f0f2f5' }}>
                  <td style={{ padding: '4px 8px' }}>{l.lote_id}</td>
                  <td style={{ padding: '4px 8px' }}>{fecha(l.creacion)}</td>
                  <td style={{ padding: '4px 8px' }}>{l.motos}</td>
                  <td style={{ padding: '4px 8px' }}>{l.confirmados} de {l.envios}</td>
                  <td style={{ padding: '4px 8px', color: Number(l.con_rechazo) > 0 ? '#b42318' : '#1f4e8c' }}>
                    {Number(l.con_rechazo) > 0
                      ? `SAP rechazó ${l.con_rechazo}: no va a Impulsa`
                      : `Esperando ${l.sin_respuesta} confirmación(es)`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Lo sincronizado: una fila por material y respuesta de Impulsa */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 6 }}>
        <span style={{ fontWeight: 700, fontSize: 13, color: '#0f2439' }}>
          Sincronizado ({datos.registro.length}{conError && !soloError ? ` · ${conError} con novedad` : ''})
        </span>
        <FormControlLabel
          sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: 12.5 } }}
          control={(
            <Checkbox
              size="small"
              checked={soloError}
              onChange={(e) => { setSoloError(e.target.checked); cargar(e.target.checked) }}
            />
          )}
          label="Solo con novedad (no existe, ambiguo…)"
        />
        <Button size="small" variant="text" onClick={() => cargar()}>Actualizar</Button>
      </div>
      <TablaPrecios columns={columnas} data={datos.registro} isLoading={isLoading} />
    </div>
  )
}

export default ImpulsaTab
